import React, { lazy, Suspense } from "react";
import environment from "web/script/modules/environment";
import classnames from "classnames";
import styles from "./product-gallery-image.css";

const ZoomableImage = lazy(() =>
    import(
        "web/react/components/product-gallery/product-gallery-zoomable-image/product-gallery-zoomable-image"
    )
);

interface NormalImageProps {
    image: ProductImage;
    onClick: () => void;
    isSingle: boolean;
    hasRelatedProductsSidebar: boolean;
    className?: string;
}

interface FullScreenImageProps {
    image: ProductImage;
}

interface ImgProps {
    image: ProductImage;
    className?: string;
}

function Img({ image, className }: ImgProps): React.ReactElement {
    const isSingleImageSrc = environment.getFeature("seo_pdp_gallery_one_image_source");

    if (isSingleImageSrc) {
        return <img alt={image.alt_text} src={image.large_url} className={className} />;
    }

    return (
        <img
            alt={image.alt_text}
            src={image.large_url}
            srcSet={`${image.large_url} 1x, ${image.large_url_2x} 2x`}
            className={className}
        />
    );
}

function NormalImage({
    image,
    onClick,
    isSingle,
    hasRelatedProductsSidebar,
    className,
}: NormalImageProps): React.ReactElement {
    return (
        <div
            className={classnames(
                styles.normalImageWrapper,
                {
                    [styles.normalImageWrapperSingle]: isSingle,
                    [styles.normalImageWrapperWithSidebar]: hasRelatedProductsSidebar,
                },
                className
            )}
            onClick={onClick}
        >
            <Img image={image} className={styles.normalImage} />
        </div>
    );
}

function FullScreenImage({ image }: FullScreenImageProps): React.ReactElement {
    return (
        <div className={styles.fullscreenImageWrapper}>
            <Suspense fallback={<Img image={image} />}>
                <ZoomableImage image={image} />
            </Suspense>
        </div>
    );
}

interface ImageProps {
    isFullscreen: boolean;
    image: ProductImage;
    onClick: () => void;
    isSingle: boolean;
    hasRelatedProductsSidebar: boolean;
    className?: string;
}

function Image({
    isFullscreen = false,
    onClick,
    image,
    isSingle,
    hasRelatedProductsSidebar,
    className,
}: ImageProps): React.ReactElement {
    if (isFullscreen) {
        return <FullScreenImage image={image} />;
    }

    return (
        <NormalImage
            className={className}
            image={image}
            onClick={onClick}
            isSingle={isSingle}
            hasRelatedProductsSidebar={hasRelatedProductsSidebar}
        />
    );
}

export default Image;
